import { useState } from "react";
import { Control, Controller, useForm } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { mutate } from "swr";
import * as yup from "yup";
import { Button } from "~/components/ui/button";
import { Label } from "~/components/ui/label";
import { MarkdownEditor } from "~/components/ui/markdown-editor";
import { MarkdownPreview } from "~/components/ui/markdown-preview";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import useToast from "~/components/ui/toast";
import { useFetcher } from "~/hooks/useFetcher";
import { usePrompt } from "~/hooks/usePrompt";
import { TabHeader } from "~/pages/manifold-nextgen/components/TabHeader";
import { useCanEditAgent } from "~/pages/manifold-nextgen/hooks/useCanEditAgent";
import { Agent, SelectableModel, agentPath, modelsPath, updateAgent } from "~/services/agents";

const UNSAVED_CHANGES_MESSAGE = "You have unsaved changes. Leave without saving?";

const schema = yup.object({
  persona: yup.string().defined(),
  instructions: yup.string().defined(),
});

type FormValues = yup.InferType<typeof schema>;

interface MarkdownFieldProps {
  name: "persona" | "instructions";
  label: string;
  description: string;
  control: Control<FormValues>;
  isEditable: boolean;
  isSaving: boolean;
}

// Viewers get the rendered markdown, borderless, matching how the Dataset
// context page presents read-only text.
const MarkdownField = ({
  name,
  label,
  description,
  control,
  isEditable,
  isSaving,
}: MarkdownFieldProps) => (
  <Controller
    name={name}
    control={control}
    render={({ field }) => (
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <Label htmlFor={name} className="text-base">
            {label}
          </Label>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        {isEditable ? (
          <MarkdownEditor
            id={name}
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            readOnly={isSaving}
            paneClassName="min-h-[15rem] max-h-[36rem] overflow-y-auto"
          />
        ) : (
          <MarkdownPreview>{field.value}</MarkdownPreview>
        )}
      </div>
    )}
  />
);

interface AgentBehaviorTabProps {
  // Overrides the outlet-context agent — used when this tab is rendered
  // outside the AgentDetail route tree (the project context sidebar).
  agent?: Agent;
  // True when this tab is rendered inside the session context sidebar,
  // which only ever shows a read-only view.
  sessionSidebarView?: boolean;
}

export const AgentBehaviorTab = ({
  agent: agentProp,
  sessionSidebarView = false,
}: AgentBehaviorTabProps) => {
  const outletAgent = useOutletContext<Agent>();
  const agent = agentProp ?? outletAgent;
  const canEditGrant = useCanEditAgent(agent);
  const isEditable = !sessionSidebarView && canEditGrant;
  const { showError } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingModel, setIsSavingModel] = useState(false);

  const { data: models } = useFetcher<SelectableModel[]>(modelsPath());
  // Always keep the agent's current model selectable, even if it is not in the
  // catalog (e.g. a value set via the API that predates the curated picker).
  const modelOptions: SelectableModel[] =
    models && !models.some((m) => m.name === agent.model)
      ? [{ name: agent.model, display_name: agent.model }, ...models]
      : (models ?? [{ name: agent.model, display_name: agent.model }]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, isValid },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: {
      persona: agent.persona ?? "",
      instructions: agent.instructions ?? "",
    },
  });

  // Warn before navigating away (tab switch, browser close/refresh) with unsaved edits.
  usePrompt(isDirty ? UNSAVED_CHANGES_MESSAGE : false, { beforeUnload: true });

  const doSave = async (values: FormValues) => {
    setIsSaving(true);
    try {
      await updateAgent(agent.id, {
        persona: values.persona,
        instructions: values.instructions,
      });
      reset(values);
      await mutate(agentPath(agent.id));
    } catch {
      showError("Failed to save agent");
    } finally {
      setIsSaving(false);
    }
  };

  // Model commits immediately on selection, same as the name/description
  // elsewhere on the agent — no separate Save step.
  const handleModelChange = async (next: string) => {
    setIsSavingModel(true);
    try {
      await updateAgent(agent.id, { model: next });
      await mutate(agentPath(agent.id));
    } catch {
      showError("Failed to update model");
    } finally {
      setIsSavingModel(false);
    }
  };

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4">
      <TabHeader
        title={sessionSidebarView ? undefined : "Behavior"}
        showReadOnlyBadge={!sessionSidebarView && !isEditable}
      />

      <div className="flex flex-col gap-1">
        <Label htmlFor="model" className="text-base">
          Model
        </Label>
        {isEditable ? (
          <Select value={agent.model} onValueChange={handleModelChange} disabled={isSavingModel}>
            <SelectTrigger id="model" aria-label="Model">
              <SelectValue placeholder="Select a model" />
            </SelectTrigger>
            <SelectContent>
              {modelOptions.map((m) => (
                <SelectItem key={m.name} value={m.name}>
                  {m.display_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <p id="model" className="text-sm text-foreground">
            {modelOptions.find((m) => m.name === agent.model)?.display_name ?? agent.model}
          </p>
        )}
      </div>

      <form onSubmit={handleSubmit(doSave)} className="flex flex-col gap-4">
        {(agent.persona || isEditable) && (
          <MarkdownField
            name="persona"
            label="Persona"
            description="Defines the agent's voice and point of view — how it introduces itself and speaks to users."
            control={control}
            isEditable={isEditable}
            isSaving={isSaving}
          />
        )}

        {(agent.instructions || isEditable) && (
          <MarkdownField
            name="instructions"
            label="Instructions"
            description="Explicit directions the agent follows on every turn, layered on top of its persona."
            control={control}
            isEditable={isEditable}
            isSaving={isSaving}
          />
        )}

        {isEditable && isDirty && (
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={!isValid} loading={isSaving}>
              Save
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => reset()}
              disabled={isSaving}
            >
              Cancel
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};
